import * as Android from '@/api/android';
import { MinOrderQtyType } from '@/enums/MinOrderQtyType';
import i18n from '@/i18n';
import Dialog from '@/interfaces/Dialog';
import Loading from '@/interfaces/Loading';
import { logger } from '@/logger';
import router from '@/router';
import { useCartStore } from '@/stores/cart';
import { useConfigStore } from '@/stores/config';
import { useMenuStore } from '@/stores/menu';
import { useModalStore } from '@/stores/modal';
import { useOrderStore } from '@/stores/order';
import { usePaymentStore } from '@/stores/payment';
import { convertVerionToNumber, posToLocaleLanguage } from '@/utils';
import { useStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';
import { computed, ref } from 'vue';

export const useCommonStore = defineStore('common', () => {
  // 대기 화면 백그라운드에서 데이터 업데이트를 처리하는 인터벌 아이디
  let updateIntervalId: ReturnType<typeof setInterval> | null = null;
  const newBannerVersion = 10000066; // 1.0.000.066
  let resolvePromise: ((value: boolean) => void) | null = null; // 다이얼로그 프로미스 해제 함수

  const updateDate = useStorage('UPDATE_DATE', '');
  const isBannerStarted = useStorage('BANNER', false);

  const configStore = useConfigStore();
  const modalStore = useModalStore();
  const orderStore = useOrderStore();
  const cartStore = useCartStore();
  const menuStore = useMenuStore();
  const paymentStore = usePaymentStore();

  const loadings = ref<Loading[]>([]); // 로딩 모달 스택
  const isDialogShow = ref(false); // 다이얼로그 표시 여부
  const dialog = ref<Dialog>(); // 다이얼로그 정보
  const dialogIntervalId = ref(); // 다이얼로그 인터벌 아이디
  const waitingPopupIntervalId = ref<ReturnType<typeof setTimeout> | null>(null); // 대기 팝업 인터벌 아이디
  const isWaitingPopupOpen = ref(false); // 대기 팝업 표시 여부
  const resetKey = ref();
  const limitType = ref();
  const isLandscapeMode = ref(window.matchMedia('(orientation: landscape)').matches);

  // 현재 활성화된 페이지 로딩 또는 모달의 로딩 조회
  const activeLoading = computed(() => {
    // 모달이 있다면 해당 모달의 아이디 조회
    const currentDialogId = dialog.value?.id || null;
    const currentModalId = modalStore.currentSyncModalId;

    // 해당 모달의 로딩 또는 전체 페이지 로딩 조회
    if (currentDialogId) {
      return loadings.value.find((loading) => loading.modalId === currentDialogId);
    } else if (currentModalId) {
      return loadings.value.find((loading) => loading.modalId === currentModalId);
    }
    return loadings.value.length > 0;
  });

  // 전체 로딩 상태 조회
  const isLoading = computed(() => loadings.value.length > 0);

  /** 로딩 표시 */
  const showLoading = () => {
    const currentDialogId = dialog.value?.id;
    const currentModalId = modalStore.currentSyncModalId;

    const loading: Loading = {
      id: uuid(),
      modalId: currentDialogId || currentModalId || null,
    };
    loadings.value.push(loading);
    logger.info('[ShowLoading] 로딩 표시', loading, loadings.value);
    return loading.id;
  };

  /** 로딩 숨김 */
  const hideLoading = (loadingId?: string) => {
    logger.info('[HideLoading] 로딩 숨김', loadingId);

    // 아이디가 없으면 전체 로딩 숨김
    if (!loadingId) loadings.value = [];

    const index = loadings.value.findIndex((loading) => loading.id === loadingId);
    if (index > -1) {
      loadings.value.splice(index, 1);
    }
  };

  /** 모든 로딩 모달 숨김 */
  const hdieAllLoading = () => (loadings.value = []);

  const showDialog = (_dialog: Dialog): Promise<boolean> => {
    logger.info('[ShowDialog] 다이얼로그 열기', _dialog);

    // 다이얼로그 고유 아이디 설정
    _dialog.id = uuid();

    dialog.value = _dialog;
    isDialogShow.value = true;

    // 타이머 설정한 경우
    if (dialog.value.autoCloseSeconds ?? 0 > 0) {
      clearInterval(dialogIntervalId.value);

      dialogIntervalId.value = setInterval(() => {
        if (!dialog.value) return;

        // 타이머 시간이 0보다 큰 경우에만 감소
        dialog.value.autoCloseSeconds!--;

        // 타이머 시간이 0인 경우 다이얼로그 닫기
        if (dialog.value.autoCloseSeconds! <= 0) {
          clearInterval(dialogIntervalId.value);
          hideDialog(false);
        }
      }, 1000);
    }

    return new Promise((resolve, _) => {
      resolvePromise = resolve;
    });
  };

  const hideDialog = (result: boolean) => {
    if (!dialog.value?.id) return;
    isDialogShow.value = false;
    if (resolvePromise) {
      dialog.value = undefined;
      resolvePromise(result);
      resolvePromise = null;
    }
  };

  /** 대기 화면 카운트다운 모달 열기 */
  const openWaitingPopup = () => {
    logger.info('[OpenWaitingPopup] 대기 팝업 열기');
    isWaitingPopupOpen.value = true;
  };

  /** 대기 화면 카운트다운 모달 닫기 */
  const closeWaitingPopup = () => {
    logger.info('[CloseWaitingPopup] 대기 팝업 닫기');
    isWaitingPopupOpen.value = false;
  };

  /**
   * 대기 화면 팝업 인터벌 설정
   * - 대기 화면이 표시 된 후에도 백그라운드에서 실시간 데이터 업데이트 및 메뉴그룹 사용시간 적용을 위해 Interval로 계속 실행
   * - 대기 화면 해제 시 실시간 데이터 업데이트 및 메뉴그룹 갱신 적용 하도록 수정 고려 (화면에 지연되어 표시되는 문제가 있음)
   */
  const setWaitingPopupInterval = () => {
    // 실행중인 인터벌 해제
    clearWaitingPopupInterval();

    // 대기 팝업 타임아웃 시간 조회 (초)
    const timeout = configStore.config.WaitingTimeout || 60;
    const countdown = configStore.config.WaitingPopupCountdown || 30;

    // 대기 팝업 인터벌 설정
    waitingPopupIntervalId.value = setInterval(async () => {
      // 결제중인 데이터가 있는 경우 종료
      if (paymentStore.summary.splits.length > 0) return;

      // 배너(광고 또는 위젯) 사용 여부
      const isBannerUsed = configStore.config.IsAdvertisementUsed || configStore.config.IsWidgetModeUsed;

      // 배너를 사용 설정인 경우
      if (isBannerUsed) {
        // 배너 표시 중 여부
        const isBannerShown = Android.isShowBanner();
        if (isBannerShown) {
          // [2024-04-18] 안드로이드 광고 해제 시 콜백 함수 호출 여부. 1.0.000.066 버전을 기준으로 분기
          const isBannerHideCallback = convertVerionToNumber(configStore.androidVersion) >= newBannerVersion;

          // 배너 종료 후 콜백 함수를 호출하지 않는 경우에만 배너 실행 중 실시간 데이터 갱신 (콜백 함수를 호출한다면 콜백 함수에서 한 번만 갱신함)
          if (!isBannerHideCallback) {
            // 품절 및 한정 데이터 갱신
            await fetchRealtimeData(false);
          }

          // 화면 리셋 후 종료
          reset();
          return;
        }

        // 배너 해제 직후에 초기화 모달이 나오지 않도록 1회는 스킵
        if (isBannerStarted.value && !isBannerShown) {
          isBannerStarted.value = false;
          return;
        }
      }

      // 대기 화면 팝업 내의 카운트다운 체크
      if (countdown === 0) {
        // 카운트다운 설정 값이 0인 경우 즉시 대기 화면으로 전환
        setWaitingState();
      } else {
        // 대기 화면 카운트다운 팝업 열기
        openWaitingPopup();
      }
    }, timeout * 1000);
  };

  /** 대기 화면 전환 인터벌 해제 */
  const clearWaitingPopupInterval = () => {
    if (waitingPopupIntervalId.value) {
      clearTimeout(waitingPopupIntervalId.value);
      waitingPopupIntervalId.value = null;
    }
  };

  /** 실시간 데이터 업데이트 인터벌 해제 */
  const clearRealtimeUpdateInterval = () => {
    if (updateIntervalId) {
      clearInterval(updateIntervalId);
      updateIntervalId = null;
    }
  };

  /** 대기 화면으로 전환 처리 */
  const setWaitingState = async () => {
    // 대기 화면 (광고 또는 위젯) 사용 여부
    const isWaitingUsed = configStore.config.IsAdvertisementUsed || configStore.config.IsWidgetModeUsed;

    // 대기 화면으로 전환
    if (isWaitingUsed) {
      isBannerStarted.value = true;
      Android.showBanner();
    }

    // 대기 팝업 닫기
    closeWaitingPopup();

    // 화면 리셋 (바로 대기 화면 해제하는 경우 리셋되는 동안 대기 팝업이 안닫히고 잠깐 보이는 현상 때문에 0.5초 이후 실행)
    setTimeout(() => {
      reset();
    }, 100);

    // 품절 및 한정 데이터 갱신
    await fetchRealtimeData(false);

    // 최소주문수량 옵션이 첫 주문에만 사용하거나
    // 후식메뉴 옵션을 사용하는 경우 주문 조회하여 저장
    if (configStore.config.MinOrderQtyType === MinOrderQtyType.FirstOnly || configStore.config.DessertType !== '0') {
      // 이미 주문된 수량 확인을 위해 주문 조회
      const response = await Android.getOrderDataForOrderAsync();

      // 주문 조회 성공 여부 검사 후 저장
      if (response.RET_CODE === 0) orderStore.setOrder(response.DATA);
    }
  };

  /** 업데이트 날짜 설정 */
  const setUpdateDate = () => {
    updateDate.value = dayjs(new Date(), 'YYYY-MM-DD HH:mm:ss').toString();
  };

  const setLimitType = (value: string) => {
    limitType.value = value;
  };

  /**
   * 사이트 초기화
   * 안드로이드 광고 API 호출하여 화면을 가린 후에 모든 모달 팝업창을 닫고, 메인 페이지를 다시 렌더링 한다.
   * @returns void
   */
  const reset = () => {
    logger.info('======================== reset ============================');

    // 대기 화면 카운트다운 팝업 닫기
    closeWaitingPopup();

    // 회원 초기화
    orderStore.setCustomer(null);

    // 장바구니 초기화
    cartStore.initializeCart();

    // 모든 모달창 닫기
    modalStore.closeAllModal();

    // 다이얼로그 닫기
    hideDialog(false);

    // 언어 초기화
    i18n.global.locale.value = posToLocaleLanguage(configStore.config.PosLanguage);

    // 모든 로딩 모달 숨김
    hideLoading();

    // 메인페이지 리셋
    resetKey.value = uuid();
    router.push('/home');
  };

  // 리셋 키만 변경 (메뉴그룹 시간 실시간 업데이트를 위해)
  const resetOnlyKey = () => {
    resetKey.value = uuid();
  };

  /**
   * 실시간 데이터 갱신
   * @param isForced 강제 실행 여부
   */
  const fetchRealtimeData = async (isForced: boolean) => {
    if (updateDate.value && !isForced) {
      // 마지막 업데이트 날짜로 부터 5분 이상 경과된 경우 데이터 다시 조회
      const now = dayjs(new Date());
      if (now.diff(updateDate.value, 's') >= 300) {
        setUpdateDate();
        try {
          // 품절 및 한정수량 업데이트
          await menuStore.fetchSoldOut();
          await menuStore.fetchLimitItem();
        } catch (error: unknown) {
          // 서버 통신이 실패하더라도 에러 무시
          logger.error('서버 통신 실패', error);
        }
      }
    } else {
      // 마지막 업데이트 날짜가 없는 경우 로컬스토리지에 날짜 저장
      setUpdateDate();
      try {
        // 품절 및 한정수량 업데이트
        await menuStore.fetchSoldOut();
        await menuStore.fetchLimitItem();
      } catch (error: unknown) {
        // 서버 통신이 실패하더라도 에러 무시
        logger.error('서버 통신 실패', error);
      }
    }
  };

  return {
    loadings,
    isDialogShow,
    dialog,
    resolvePromise,
    waitingPopupIntervalId,
    isWaitingPopupOpen,
    resetKey,
    updateDate,
    limitType,
    isLandscapeMode,

    activeLoading,
    isLoading,

    showLoading,
    hideLoading,
    hdieAllLoading,
    showDialog,
    hideDialog,
    closeWaitingPopup,
    setWaitingPopupInterval,
    clearWaitingPopupInterval,
    clearRealtimeUpdateInterval,
    setWaitingState,
    setUpdateDate,
    setLimitType,
    resetOnlyKey,
    fetchRealtimeData,
  };
});
